import { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { LifecycleBadge } from "../LifecycleBadge";
import { CheckCircle2, Circle, Clock, CalendarDays } from "lucide-react";

const STEP_ACTIONS = [
  { stage: "in_transit", label: "Mark In Transit" },
  { stage: "delivered", label: "Mark Delivered" },
  { stage: "epod", label: "e-POD Received" },
  { stage: "original_pod", label: "Original POD Received" },
];

export function Lifecycle({ lr, onChanged }) {
  const lc = lr.lifecycle;
  const [stage, setStage] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  if (!lc) return null;
  const tl = lc.timeline || [];
  const doneKeys = new Set(tl.filter((s) => s.done).map((s) => s.key));
  // Only offer stages that haven't been reached yet — no redundant "Mark In Transit"
  // once the LR is already in transit/delivered. (Backdating a done stage still works
  // by re-selecting it from the timeline flow.)
  const availableActions = STEP_ACTIONS.filter((a) => !doneKeys.has(a.stage));

  async function save() {
    if (!stage) { toast.error("Pick a stage to update"); return; }
    setSaving(true);
    try {
      await api.patch(`/lrs/${lr.id}/stage-event`, { stage, date });
      toast.success("Lifecycle updated");
      setStage("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-lifecycle">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-wide text-[#68665E]">Lifecycle</div>
        <LifecycleBadge lifecycle={lc} />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-3 mb-4">
        {tl.map((s) => {
          const isPending = s.key === lc.pending_key;
          const Icon = s.done ? CheckCircle2 : isPending ? Clock : Circle;
          const color = s.done ? "text-[#2F6F4E]" : isPending ? "text-[#C84B31]" : "text-[#C9C7BF]";
          return (
            <div key={s.key} className="flex items-center gap-2" data-testid={`lifecycle-step-${s.key}`}>
              <Icon className={`w-4 h-4 shrink-0 ${color}`} />
              <div>
                <div className={`text-xs ${s.done || isPending ? "text-[#1A1A18] font-medium" : "text-[#68665E]"}`}>{s.label}</div>
                <div className="text-[10px] text-[#A09E96]">
                  {s.at ? String(s.at).slice(0, 10) : isPending ? `⏳ ${lc.days ?? 0}d pending` : "—"}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-end gap-2 border-t border-[#EEEDE8] pt-3">
        {availableActions.length === 0 ? (
          <div className="text-xs text-[#2F6F4E]" data-testid="lifecycle-all-done">All lifecycle stages complete.</div>
        ) : (
        <>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#68665E] block mb-1">Update stage</label>
          <select className="input-field !py-1.5 text-sm" value={stage} onChange={(e) => setStage(e.target.value)} data-testid="lifecycle-stage-select">
            <option value="">— select —</option>
            {availableActions.map((a) => <option key={a.stage} value={a.stage}>{a.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#68665E] mb-1 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Date</label>
          <input type="date" className="input-field !py-1.5 text-sm" value={date} onChange={(e) => setDate(e.target.value)} data-testid="lifecycle-date" />
        </div>
        <button onClick={save} disabled={saving} className="btn-primary !py-1.5 text-sm" data-testid="lifecycle-save">{saving ? "Saving…" : "Save"}</button>
        </>
        )}
      </div>
      <p className="text-[11px] text-[#A09E96] mt-2">Backdate any stage — e.g. if it was delivered 2 days ago, pick that date.</p>
    </div>
  );
}
